import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { isNil } from 'lodash';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FeedbackService } from '@app/core/services';
import { ExtendedFile } from '@app/interfaces';
import { UploadedFileModel } from '@app/shared/components/file-upload/models/uploaded-file.model';
import { FileUploadDnDComponent } from '../file-upload-dnd/file-upload-dnd.component';
import { NoDataAvailableComponent } from '../no-data-available/no-data-available.component';

@Component({
    selector: 'image-preview',
    templateUrl: './image-preview.component.html',
    styleUrls: ['./image-preview.component.less'],
    standalone: true,
    imports: [
        NgIf,
        FileUploadDnDComponent,
        NoDataAvailableComponent,
        AsyncPipe,
    ],
})
export class ImagePreviewComponent implements OnInit, OnDestroy {
	@Output() imageChangeEmitter = new EventEmitter<UploadedFileModel>();
	/** Used to set the initial image URL. This should only be used when the bindToUrl prop is true. */
	@Input() set imageUrl(value: string) {
		if (!isNil(value)) {
			this.uploadedFile$.next({ url: value });
		}
		else if (this.bindToUrl) {
			this.remove();
		}
	}
	/** Used to set the initial image file. This should only be used when the bindToUrl prop is false. */
	@Input() set uploadedFile(value: UploadedFileModel | string) {
		const fileModel = this.getFileModel(value);
		if (!isNil(fileModel)) {
			this.uploadedFile$.next(fileModel);
		}
		else if (!this.bindToUrl) {
			this.remove();
		}
	}
	@Input() readOnly = false;
	@Input() heightProp = 170;
	/** Text to be displayed as a header above the image */
	@Input() labelText: string;
	/** Text to be displayed in the component when there is no image */
	@Input() noDataMessage = 'No image available';
	/**
	 * Indicates whether the component should bind to a URL for the image (such as a Vault location).
	 * The default is to bind to the binary for the file itself.
	 */
	@Input() bindToUrl = false;
	/** Indicates whether the control should display the file extensions the user is allowed to select. */
	@Input() showAllowedExtensions = true;

	// TODO: Potential future enhancement - allow components to specify allowed file extensions via an input property.
	public readonly imageExtensions: string[] = ['.bmp', '.gif', '.jpg', '.jpeg', '.jpe', '.png', '.svg', '.tif', '.tiff'];
	protected readonly allowedExtensionsMessage = `Allowed file extensions are ${this.imageExtensions.join(', ')}`;
	private readonly destroy$ = new Subject();
	public uploadedFile$ = new BehaviorSubject<UploadedFileModel>(null);

	constructor(private readonly sanitizer: DomSanitizer, private readonly feedbackService: FeedbackService) {}

	ngOnInit(): void {
		this.uploadedFile$.pipe(takeUntil(this.destroy$)).subscribe((uploadedFileModel) => {
			this.imageChangeEmitter.emit(uploadedFileModel);
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	public handleSelectedFiles(files: ExtendedFile[]): void {
		if (files.length === 0) {
			return;
		}

		if (this.imageExtensions?.length && !this.imageExtensions.find((ext) => files[0].name.toLowerCase().endsWith(ext))) {
			this.feedbackService.error(`Invalid file type selected.  ${this.allowedExtensionsMessage}.`);
			return;
		}

		if (this.bindToUrl) {
			this.uploadedFile$.next({ file: files[0], url: URL.createObjectURL(files[0]) } as UploadedFileModel);
		}
		else {
			this.loadFileBinary(files[0]);
		}
	}

	private loadFileBinary(file: ExtendedFile) {
		const reader = new FileReader();

		reader.onload = (e: any) => {
			const uploadedFile: UploadedFileModel = { fileName: file.name, file };

			const fileBase64Encoded = e.target.result.split(',')[1]; //Only care about the text after "base64,"
			uploadedFile.fileBase64Encoded = fileBase64Encoded;
			this.uploadedFile$.next(uploadedFile);
		};

		reader.readAsDataURL(file);
	}

	remove(): void {
		this.uploadedFile$.next(null);
	}

	sanitize(url: string): SafeUrl {
		return this.sanitizer.bypassSecurityTrustUrl(url);
	}

	private getFileModel(file: UploadedFileModel | string): UploadedFileModel {
		if (isNil(file)) {
			return null;
		}

		if (typeof file === 'string') {
			// Assume all image files are encoded
			return file.length > 0 ? { fileBase64Encoded: file } : null;
		}

		return file;
	}
}
